import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from "@ngrx/store";
import {
  MyProfilePageActions,
  SkillSearchBarActions,
  ConsultantSkillProficiencyFilterActions,
  ConsultantSkillApiActions,
  AddUserSkillDialogueActions,
  ViewProfilePageActions,
} from "../../consultant-skill/actions";
import { ISkillNode } from "../../models/skillNode";
import { SkillAliasPageActions } from "@skills-app/skill-alias/actions";
import {
  SelectedSkillActions,
  SkillsPageActions,
  SkillApiActions,
  EditSkillActions,
  DeleteSkillActions,
  AddRelatedSkillsActions,
} from "../actions";
import { NavmenuActions } from "@skills-app/core/actions";
import { SkillProficiencyRange } from "@skills-app/models/skillProficiencyRange";
import { SearchBySkillOrConsultantPageActions } from "@skills-app/consultant-skill/actions";
import { ThingSpacePageActions } from "@skills-app/thingspace/actions";
import { HomePageActions } from "@skills-app/home/Actions";
import { AddSkillToProfileActions } from "@skills-app/shared/actions";
import { ISkillGroup } from "@skills-app/models/skillGroup";
import { SkillGroupApiActions } from "@skills-app/skill-groups/actions";
import { SortType } from "@skills-app/models/sorting";

export interface ISkillsState {
  skillNodes: ISkillNode[];
  skillsNodesByRelatedSkillsThreshold: ISkillNode[];
  skillsForSelectedSearch: ISkillNode[];
  selectedSkillsId: string[];
  selectedSkillRelatedSkills: ISkillNode[];
  selectedSkillAliases: ISkillNode[];
  selectedSkillGroups: ISkillGroup[];
  filteredByRelatedSkillsThreshold: boolean;
  relatedSkillsThreshold: number;
  selectedSkillProficiencyRange: SkillProficiencyRange;
  isLoading: ISkillLoading;
  sortAscending: boolean;
  sortType: SortType;
  onlyRecent: boolean;
}

export interface ISkillLoading {
  skills: boolean;
  selectedRelatedSkills: boolean;
  selectedSkillAliases: boolean;
  addSkill: boolean;
  addUserSkill: boolean;
  editSkill: boolean;
  deleteSkill: boolean;
  addRelatedSkill: boolean;
  deleteRelatedSkill: boolean;
  skillsForSearch: boolean;
}

const initialState: ISkillsState = {
  skillNodes: [],
  skillsNodesByRelatedSkillsThreshold: [],
  skillsForSelectedSearch: [],
  filteredByRelatedSkillsThreshold: false,
  relatedSkillsThreshold: 0,
  selectedSkillsId: [],
  selectedSkillRelatedSkills: [],
  selectedSkillGroups: [],
  selectedSkillAliases: [],
  selectedSkillProficiencyRange: { min: 2, max: 6 },
  sortType: 0,
  sortAscending: true,
  onlyRecent: false,
  isLoading: {
    skills: false,
    selectedRelatedSkills: false,
    selectedSkillAliases: false,
    addSkill: false,
    addUserSkill: false,
    editSkill: false,
    deleteSkill: false,
    addRelatedSkill: false,
    deleteRelatedSkill: false,
    skillsForSearch: false,
  },
};

export const reducer = createReducer(
  initialState,

  on(SkillApiActions.loadSkillsForSelectedSearchSuccess, (state, action) => ({
    ...state,
    skillsForSelectedSearch: action.skills,
    isLoading: { ...state.isLoading, skillsForSearch: false },
  })),

  on(SkillApiActions.loadSkillsForSelectedSearchFailure, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, skillsForSearch: false },
  })),

  on(
    SkillsPageActions.loadPage,
    HomePageActions.loadPage,
    MyProfilePageActions.loadPage,
    ViewProfilePageActions.loadPage,
    ThingSpacePageActions.loadPage,
    SkillSearchBarActions.loadComponent,
    SkillAliasPageActions.loadPage,
    SearchBySkillOrConsultantPageActions.loadPage,
    (state) => ({
      ...state,
      selectedSkillsId: [],
      selectedSkillRelatedSkills: [],
      selectedSkillGroups: [],
      filteredByRelatedSkillsThreshold: false,
      isLoading: { ...state.isLoading, skills: true },
    }),
  ),

  on(
    SkillsPageActions.toggleFilteredByRelatedSkillsThreshold,
    (state, action) => ({
      ...state,
      filteredByRelatedSkillsThreshold: !state.filteredByRelatedSkillsThreshold,
      relatedSkillsThreshold: action.relatedSkillsThreshold,
      isLoading: { ...state.isLoading },
    }),
  ),

  on(MyProfilePageActions.toggleSelectSkill, (state, action) => ({
    ...state,
    selectedSkillsId: state.selectedSkillsId.some((s) => s === action.skillId)
      ? state.selectedSkillsId.filter((s) => s !== action.skillId)
      : [...state.selectedSkillsId, action.skillId],
  })),

  // Skills page selection (full skill object)
  on(SkillsPageActions.selectSkill, (state, action) => {
    const skillId = action.skill.Id;
    if (!skillId) return state;

    return {
      ...state,
      selectedSkillsId: [skillId],
      isLoading: {
        ...state.isLoading,
        selectedRelatedSkills: true,
        selectedSkillAliases: true,
      },
    };
  }),

  // Consultant search selection (id-only)
  on(SearchBySkillOrConsultantPageActions.selectSkill, (state, action) => {
    const skillId = action.skillId;
    if (!skillId) return state;

    return {
      ...state,
      selectedSkillsId: [skillId],
      isLoading: {
        ...state.isLoading,
        selectedRelatedSkills: true,
        selectedSkillAliases: true,
      },
    };
  }),

  on(
    MyProfilePageActions.selectSkill,
    ThingSpacePageActions.selectSkill,
    ConsultantSkillApiActions.selectSkill,
    AddUserSkillDialogueActions.selectSkill,
    (state, action) => {
      const skillId = action.skillId;
      if (!skillId) return state;

      return {
        ...state,
        selectedSkillsId: [skillId],
      };
    },
  ),

  on(
    MyProfilePageActions.requestUpdateUserSkill,
    AddUserSkillDialogueActions.requestUpdateUserSkill,
    (state, action) => {
      const skillId = action.skillMapping?.Id;
      if (!skillId) return state;

      return {
        ...state,
        selectedSkillsId: [skillId],
      };
    },
  ),

  on(
    NavmenuActions.endNavigation,
    SelectedSkillActions.deleteSkillDialogClosed,
    (state) => ({
      ...state,
      selectedSkillsId: [],
      selectedSkillRelatedSkills: [],
      selectedSkillGroups: [],
      isLoading: { ...state.isLoading },
    }),
  ),

  on(
    SkillsPageActions.clearSelectedSkill,
    AddUserSkillDialogueActions.clearSelectedSkill,
    MyProfilePageActions.requestDeleteUserSkill,
    MyProfilePageActions.requestDeleteMultipleUserSkills,
    AddSkillToProfileActions.requestDeleteUserSkill,
    AddSkillToProfileActions.clearSelectedSkill,
    (state) => ({
      ...state,
      selectedSkillsId: [],
    }),
  ),

  on(SkillApiActions.loadSelectedRelatedSkillsSuccess, (state, action) => ({
    ...state,
    selectedSkillRelatedSkills: action.skills,
    isLoading: { ...state.isLoading, selectedRelatedSkills: false },
  })),

  on(SkillApiActions.loadSelectedRelatedSkillsFailure, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, selectedRelatedSkills: false },
  })),

  on(SkillApiActions.loadSelectedSkillAliasesSuccess, (state, action) => ({
    ...state,
    selectedSkillAliases: action.skills,
    isLoading: { ...state.isLoading, selectedSkillAliases: false },
  })),

  on(SkillApiActions.loadSelectedSkillAliasesFailure, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, selectedSkillAliases: false },
  })),

  on(SkillApiActions.loadSkillsSuccess, (state, action) => ({
    ...state,
    skillNodes: action.skills,
    isLoading: { ...state.isLoading, skills: false },
  })),

  on(SkillApiActions.loadSkillsFailure, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, skills: false },
  })),

  on(
    SkillApiActions.loadSkillsByRelatedSkillsThresholdSuccess,
    (state, action) => ({
      ...state,
      skillsNodesByRelatedSkillsThreshold: action.skills,
    }),
  ),

  on(SkillsPageActions.requestAddSkill, (state, action) => ({
    ...state,
    skillNodes: [
      ...state.skillNodes,
      {
        Id: `TEMP-${action.addSkillRequest.Name}`,
        Name: action.addSkillRequest.Name,
        TypeId: action.addSkillRequest.SkillType?.Id ?? "",
        IsActive: true,
        SkillType: action.addSkillRequest.SkillType,
        ParentSkillNode: null,
      } as ISkillNode,
    ].sort((a, b) => a.Name.localeCompare(b.Name)),
    isLoading: { ...state.isLoading, addSkill: true },
  })),

  on(SkillsPageActions.requestForceAddSkill, (state, action) => ({
    ...state,
    skillNodes: [
      ...state.skillNodes,
      {
        Id: `TEMP-${action.addSkillRequest.Name}`,
        Name: action.addSkillRequest.Name,
        TypeId: action.addSkillRequest.SkillType?.Id ?? "",
        SkillType: action.addSkillRequest.SkillType,
        ParentSkillNode: null,
      } as ISkillNode,
    ].sort((a, b) => a.Name.localeCompare(b.Name)),
    isLoading: { ...state.isLoading, addSkill: true },
  })),

  on(SkillApiActions.skillAdded, (state, action) => {
    const existing = [...state.skillNodes].find(
      (skill) => skill.Name === action.createdSkill.Name,
    );

    return {
      ...state,
      skillNodes: [
        ...state.skillNodes,
        {
          ...(existing ?? {}),
          Id: action.createdSkill.Id,
        } as ISkillNode,
      ]
        .filter(
          (skill) =>
            !(skill.Id == null && skill.Name === action.createdSkill.Name),
        )
        .sort((a, b) => a.Name.localeCompare(b.Name)),
      isLoading: { ...state.isLoading, addSkill: false },
    };
  }),

  on(SkillApiActions.addSkillError, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, addSkill: false },
  })),

  on(EditSkillActions.requestEditSkill, (state, action) => ({
    ...state,
    skillNodes: [
      ...state.skillNodes.filter((skill) => skill.Id !== action.skill.Id),
      action.skill,
    ].sort((a, b) => a.Name.localeCompare(b.Name)),
    isLoading: { ...state.isLoading, editSkill: true },
  })),

  on(EditSkillActions.requestForceEditSkill, (state, action) => ({
    ...state,
    skillNodes: [
      ...state.skillNodes.filter((skill) => skill.Id !== action.skill.Id),
      action.skill,
    ].sort((a, b) => a.Name.localeCompare(b.Name)),
    isLoading: { ...state.isLoading, editSkill: true },
  })),

  on(SkillApiActions.skillEdited, SkillApiActions.editSkillError, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, editSkill: false },
  })),

  on(DeleteSkillActions.requestDeleteSkill, (state, action) => ({
    ...state,
    skillNodes: state.skillNodes.filter((skill) => skill.Id !== action.skillId),
    isLoading: { ...state.isLoading, deleteSkill: true },
  })),

  on(
    SkillApiActions.skillDeleted,
    SkillApiActions.deleteSkillError,
    (state) => ({
      ...state,
      isLoading: { ...state.isLoading, deleteSkill: false },
    }),
  ),

  on(AddRelatedSkillsActions.requestAddRelatedSkill, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, addRelatedSkill: true },
  })),

  on(
    SkillApiActions.relatedSkillAdded,
    SkillApiActions.addRelatedSkillFailure,
    (state) => ({
      ...state,
      isLoading: { ...state.isLoading, addRelatedSkill: false },
    }),
  ),

  on(AddRelatedSkillsActions.requestDeleteRelatedSkill, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, deleteRelatedSkill: true },
  })),

  on(
    SkillApiActions.relatedSkillDeleted,
    SkillApiActions.deleteRelatedSkillFailure,
    (state) => ({
      ...state,
      isLoading: { ...state.isLoading, deleteRelatedSkill: false },
    }),
  ),

  on(ConsultantSkillProficiencyFilterActions.setFilter, (state, action) => ({
    ...state,
    selectedSkillProficiencyRange: action,
  })),

  on(
    ConsultantSkillProficiencyFilterActions.loadComponent,
    ConsultantSkillProficiencyFilterActions.unloadComponent,
    (state) => ({
      ...state,
      selectedSkillProficiencyRange: { min: 2, max: 6 },
    }),
  ),

  on(SkillGroupApiActions.skillGroupsForSkillNodeSuccess, (state, action) => ({
    ...state,
    selectedSkillGroups: action.skillGroups,
  })),

  on(SkillsPageActions.updateSort, (state, action) => ({
    ...state,
    sortType: action?.value?.SortType ?? state.sortType,
    sortAscending: action?.value?.Ascending ?? state.sortAscending,
  })),

  on(SkillsPageActions.setOnlyRecentSkillNodes, (state, action) => ({
    ...state,
    onlyRecent: action.value,
  })),
);

export const getSkillState = createFeatureSelector<ISkillsState>("skills");

export const getSkillNodes = createSelector(getSkillState, (skillState) => {
  return skillState.filteredByRelatedSkillsThreshold
    ? skillState.skillsNodesByRelatedSkillsThreshold
    : skillState.skillNodes;
});

export const getSelectedSkillId = createSelector(
  getSkillState,
  (state) => state.selectedSkillsId[0],
);

export const getSelectedSkillsId = createSelector(
  getSkillState,
  (state) => state.selectedSkillsId,
);

export const getSelectedSkill = createSelector(
  getSkillState,
  getSelectedSkillId,
  (skillState, selectedSkillId) =>
    skillState.skillNodes.find((sn) => sn.Id === selectedSkillId),
);

export const getSelectedSkills = createSelector(
  getSkillState,
  getSelectedSkillsId,
  (skillState, selectedSkillsId) =>
    skillState.skillNodes.filter(
      (sn) => sn.Id != null && selectedSkillsId.includes(sn.Id),
    ),
);

export const getSkillsForSelectedSearch = createSelector(
  getSkillState,
  (skillState) => skillState.skillsForSelectedSearch,
);

export const getSelectedSkillRelatedSkills = createSelector(
  getSkillState,
  (skillState) => skillState.selectedSkillRelatedSkills,
);

export const getSelectedSkillSkillGroups = createSelector(
  getSkillState,
  (skillState) => skillState.selectedSkillGroups,
);

export const getSelectedSkillAliasSkills = createSelector(
  getSkillState,
  (skillState) => skillState.selectedSkillAliases,
);

export const getSkillLoading = createSelector(
  getSkillState,
  (skillState) => skillState.isLoading,
);

export const getSelectedSkillProficiencyRange = createSelector(
  getSkillState,
  (skillState) => skillState.selectedSkillProficiencyRange,
);

export const getSortedSkillNodes = createSelector(
  getSkillState,
  getSkillNodes,
  (skillsState, skillNodes) => {
    let currentSkillNodes = [...skillNodes];

    if (skillsState.onlyRecent) {
      const cutoffDate = new Date();
      cutoffDate.setMonth(cutoffDate.getMonth() - 3);

      currentSkillNodes = currentSkillNodes.filter((x) => {
        const t = x.LastModifiedTime;
        if (!t) return false;
        return new Date(t) > cutoffDate;
      });
    }

    if (skillsState.sortType === SortType.Name) {
      currentSkillNodes = currentSkillNodes.sort((a, b) =>
        a.Name.localeCompare(b.Name),
      );
    } else {
      currentSkillNodes = currentSkillNodes.sort((a, b) => {
        const ta = a.LastModifiedTime
          ? new Date(a.LastModifiedTime).getTime()
          : 0;
        const tb = b.LastModifiedTime
          ? new Date(b.LastModifiedTime).getTime()
          : 0;
        return ta - tb;
      });
    }

    return skillsState.sortAscending
      ? currentSkillNodes
      : currentSkillNodes.reverse();
  },
);
